import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { formatDateTime, formatDuration, formatNumber, formatRelative, formatPreciseTime } from '../format';
import type { LocationSummary, LogEvent, LogFilterState, LogLevel, LogSummary, SyncRun } from '../types';
import { StatTile } from '../components/StatTile';
import { FilterPanel } from '../components/FilterPanel';

interface Props {
  locations: LocationSummary[];
}

const DEFAULT_FILTERS: LogFilterState = {
  level: 'info',
  categories: [],
  locationId: '',
  runId: '',
  q: '',
  hours: 24,
};

const CATEGORY_LABELS: Record<string, string> = {
  sync: 'Sync',
  highlevel: 'HighLevel API',
  recordings: 'Recordings',
  leads: 'Lead scores',
  contacts: 'Contacts',
  users: 'Users',
  webhooks: 'Webhooks',
  auth: 'Sign-in',
  action: 'Dashboard actions',
  ai: 'AI',
  assistant: 'Assistant',
  system: 'System',
};

const RANGES: { hours: number; label: string }[] = [
  { hours: 1, label: '1 hour' },
  { hours: 24, label: '24 hours' },
  { hours: 24 * 7, label: '7 days' },
  { hours: 0, label: 'All kept' },
];

function LevelBadge({ level }: { level: LogLevel }) {
  return <span className={`level level-${level}`}>{level}</span>;
}

function time(epochMs: number): string {
  return formatPreciseTime(epochMs);
}

/** The short facts worth reading inline; the full data is one click away. */
function inlineFacts(data: Record<string, unknown> | null): string {
  if (!data) return '';
  const keys = ['method', 'status', 'path', 'count', 'error', 'reason', 'durationMs'];
  return keys
    .filter((key) => data[key] !== undefined && data[key] !== null && typeof data[key] !== 'object')
    .map((key) => `${key}: ${String(data[key])}`)
    .join(' · ');
}

function runDuration(run: SyncRun): string {
  if (!run.finishedAt) return 'running';
  return formatDuration(Math.max(1, Math.round((run.finishedAt - run.startedAt) / 1000)));
}

/**
 * The Logs tab: what the server did and why, newest first. Every sync run
 * gets a row in the history; every line can be traced to its run and
 * sub-account.
 */
export function Logs({ locations }: Props) {
  const [filters, setFilters] = useState<LogFilterState>(DEFAULT_FILTERS);
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState<LogSummary | null>(null);
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [live, setLive] = useState(true);
  const [showRuns, setShowRuns] = useState(true);
  // Requests can race (a refresh while a filter changes); only the newest lands.
  const generation = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => (current.q === search ? current : { ...current, q: search }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    (quiet: boolean) => {
      const mine = ++generation.current;
      if (!quiet) setLoading(true);
      Promise.all([api.logEvents(filters), api.logSummary(), api.syncRuns(30)])
        .then(([eventResponse, summaryResponse, runResponse]) => {
          if (mine !== generation.current) return;
          setEvents(eventResponse.events);
          setHasMore(eventResponse.hasMore);
          setSummary(summaryResponse);
          setRuns(runResponse.runs);
          setError(null);
        })
        .catch((cause) => {
          if (mine === generation.current) setError(cause instanceof ApiError ? cause.message : String(cause));
        })
        .finally(() => mine === generation.current && setLoading(false));
    },
    [filters],
  );

  useEffect(() => load(false), [load]);

  // Live mode keeps the newest page fresh, but leaves an older, paged-in view
  // alone so the reader doesn't lose their place.
  const pagedIn = events.length > 200;
  useEffect(() => {
    if (!live || pagedIn) return;
    const timer = setInterval(() => load(true), 10_000);
    return () => clearInterval(timer);
  }, [live, pagedIn, load]);

  function loadOlder() {
    const oldest = events[events.length - 1];
    if (!oldest) return;
    setLoading(true);
    api
      .logEvents(filters, oldest.id)
      .then((response) => {
        setEvents((current) => [...current, ...response.events]);
        setHasMore(response.hasMore);
      })
      .catch((cause) => setError(cause instanceof ApiError ? cause.message : String(cause)))
      .finally(() => setLoading(false));
  }

  function update(patch: Partial<LogFilterState>) {
    setFilters((current) => ({ ...current, ...patch }));
  }

  function toggleCategory(category: string) {
    update({
      categories: filters.categories.includes(category)
        ? filters.categories.filter((item) => item !== category)
        : [...filters.categories, category],
    });
  }

  function toggleExpanded(id: number) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function reset() {
    setSearch('');
    setFilters(DEFAULT_FILTERS);
  }

  const categories = [...new Set([...(summary?.categories ?? []), ...Object.keys(CATEGORY_LABELS)])];
  const activeCount =
    (filters.level !== DEFAULT_FILTERS.level ? 1 : 0) +
    filters.categories.length +
    (filters.locationId ? 1 : 0) +
    (filters.runId ? 1 : 0) +
    (filters.q ? 1 : 0) +
    (filters.hours !== DEFAULT_FILTERS.hours ? 1 : 0);
  const lastRun = runs[0];

  return (
    <div className="stack">
      {error ? (
        <div className="banner error">
          <span>{error}</span>
          <button type="button" className="btn btn-sm" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="grid grid-kpi">
        <StatTile label="Errors · 24h" value={summary ? formatNumber(summary.last24h.error) : '—'} />
        <StatTile label="Warnings · 24h" value={summary ? formatNumber(summary.last24h.warn) : '—'} />
        <StatTile label="Info · 24h" value={summary ? formatNumber(summary.last24h.info) : '—'} />
        <StatTile
          label="Last sync"
          value={lastRun ? formatRelative(lastRun.startedAt) : '—'}
          footnote={
            lastRun
              ? `${lastRun.lane ?? ''} ${lastRun.trigger} · ${runDuration(lastRun)} · ${
                  lastRun.status === 'errors' ? `${lastRun.errors.length} failed` : lastRun.status
                }`
              : undefined
          }
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Sync runs</h2>
          <button type="button" className="btn btn-sm" onClick={() => setShowRuns((value) => !value)}>
            {showRuns ? 'Hide' : 'Show'}
          </button>
        </div>
        <p className="card-sub">
          One row per sync. Select a run to see only its log lines.
        </p>
        {showRuns ? (
          <div className="table-wrap runs-table">
            <table>
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Type</th>
                  <th>Took</th>
                  <th>Status</th>
                  <th className="num">Sub-accounts</th>
                  <th className="num">Calls</th>
                  <th className="num">Appts</th>
                  <th className="num">Opps</th>
                  <th className="num">Contacts</th>
                  <th className="num">Recordings</th>
                  <th className="num">API calls</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className={filters.runId === String(run.id) ? 'selected' : undefined}>
                    <td className="nowrap">
                      {formatDateTime(run.startedAt)}
                      <div className="cell-sub">#{run.id}</div>
                    </td>
                    <td className="nowrap">
                      {run.lane ?? '—'}
                      <div className="cell-sub">{run.trigger}</div>
                    </td>
                    <td className="nowrap">
                      {runDuration(run)}
                      {run.enrichmentFinishedAt ? (
                        <div className="cell-sub">
                          + {formatDuration(Math.max(1, Math.round((run.enrichmentFinishedAt - (run.finishedAt ?? run.startedAt)) / 1000)))} fill-in
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span className={`level level-${run.status === 'errors' ? 'error' : run.status === 'running' ? 'debug' : 'ok'}`}>
                        {run.status === 'errors' ? `${run.errors.length} failed` : run.status}
                      </span>
                      {run.errors.length ? (
                        <div className="cell-sub" title={run.errors.map((item) => item.message).join('\n')}>
                          {run.errors[0]!.message.slice(0, 60)}
                          {run.errors[0]!.message.length > 60 ? '…' : ''}
                        </div>
                      ) : null}
                    </td>
                    <td className="num">
                      {formatNumber(run.locations)}
                      {run.skipped ? <div className="cell-sub">{formatNumber(run.skipped)} skipped</div> : null}
                    </td>
                    <td className="num">{formatNumber(run.calls)}</td>
                    <td className="num">{formatNumber(run.appointments)}</td>
                    <td className="num">{formatNumber(run.opportunities)}</td>
                    <td className="num">{formatNumber(run.contacts)}</td>
                    <td className="num">{formatNumber(run.recordings)}</td>
                    <td className="num">
                      {formatNumber(run.requests)}
                      {run.requestFailures || run.rateLimited ? (
                        <div className="cell-sub">
                          {run.requestFailures ? `${formatNumber(run.requestFailures)} failed` : ''}
                          {run.requestFailures && run.rateLimited ? ' · ' : ''}
                          {run.rateLimited ? `${formatNumber(run.rateLimited)} throttled` : ''}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() =>
                          update({ runId: filters.runId === String(run.id) ? '' : String(run.id), hours: 0 })
                        }
                      >
                        {filters.runId === String(run.id) ? 'All lines' : 'Lines'}
                      </button>
                    </td>
                  </tr>
                ))}
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="empty">
                      No sync runs recorded yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <FilterPanel
        activeCount={activeCount}
        onReset={reset}
        actions={
          <>
            <label className="switch small">
              <input type="checkbox" role="switch" checked={live} onChange={(event) => setLive(event.target.checked)} />
              Live
            </label>
            <a className="btn btn-sm btn-ghost" href={api.logExportUrl(filters)} download>
              Export CSV
            </a>
          </>
        }
      >
        <div className="field">
          <label>Time</label>
          <div className="segmented" role="group" aria-label="Time range">
            {RANGES.map((range) => (
              <button
                type="button"
                key={range.hours}
                aria-pressed={filters.hours === range.hours}
                onClick={() => update({ hours: range.hours })}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label>Level</label>
          <div className="segmented" role="group" aria-label="Minimum level">
            {(
              [
                ['', 'All'],
                ['info', 'Info+'],
                ['warn', 'Warnings+'],
                ['error', 'Errors'],
              ] as [LogFilterState['level'], string][]
            ).map(([value, label]) => (
              <button type="button" key={value || 'all'} aria-pressed={filters.level === value} onClick={() => update({ level: value })}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="logs-location">Sub-account</label>
          <select id="logs-location" value={filters.locationId} onChange={(event) => update({ locationId: event.target.value })}>
            <option value="">All sub-accounts</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="logs-run">Sync run</label>
          <select id="logs-run" value={filters.runId} onChange={(event) => update({ runId: event.target.value })}>
            <option value="">Any run</option>
            {runs.map((run) => (
              <option key={run.id} value={String(run.id)}>
                #{run.id} · {formatDateTime(run.startedAt)}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-grow">
          <label htmlFor="logs-search">Search</label>
          <input
            id="logs-search"
            type="search"
            placeholder="Message, path, error…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="field field-full">
          <label>Category</label>
          <div className="chips">
            {categories.map((category) => (
              <button
                type="button"
                className="chip"
                key={category}
                aria-pressed={filters.categories.includes(category)}
                onClick={() => toggleCategory(category)}
              >
                {CATEGORY_LABELS[category] ?? category}
              </button>
            ))}
          </div>
        </div>
      </FilterPanel>

      <div className={`card${loading ? ' reloading' : ''}`}>
        <div className="card-head">
          <h2>Log</h2>
          <span className="small muted">
            {formatNumber(events.length)}
            {hasMore ? '+' : ''} line{events.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="table-wrap">
          <table className="log-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Level</th>
                <th>Category</th>
                <th>Sub-account</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const open = expanded.has(event.id);
                const facts = inlineFacts(event.data);
                return (
                  <Fragment key={event.id}>
                    <tr
                      className={`log-row log-${event.level}${event.data ? ' expandable' : ''}`}
                      onClick={() => event.data && toggleExpanded(event.id)}
                    >
                      <td className="nowrap mono small">{time(event.ts)}</td>
                      <td>
                        <LevelBadge level={event.level} />
                      </td>
                      <td className="nowrap small">{CATEGORY_LABELS[event.category] ?? event.category}</td>
                      <td className="small">
                        {event.locationId ? (
                          <button
                            type="button"
                            className="link-button"
                            onClick={(click) => {
                              click.stopPropagation();
                              update({ locationId: event.locationId! });
                            }}
                          >
                            {event.locationName ?? event.locationId}
                          </button>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className="cell-strong">{event.message}</span>
                        {event.runId ? (
                          <button
                            type="button"
                            className="link-button run-link"
                            onClick={(click) => {
                              click.stopPropagation();
                              update({ runId: String(event.runId), hours: 0 });
                            }}
                          >
                            run #{event.runId}
                          </button>
                        ) : null}
                        {facts ? <div className="cell-sub">{facts}</div> : null}
                      </td>
                    </tr>
                    {open ? (
                      <tr className="log-detail">
                        <td colSpan={5}>
                          <pre>{JSON.stringify(event.data, null, 2)}</pre>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {!loading && events.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty">
                    Nothing logged that matches these filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {hasMore ? (
          <div className="pagination">
            <span>Showing the newest {formatNumber(events.length)} lines.</span>
            <button type="button" className="btn btn-sm" disabled={loading} onClick={loadOlder}>
              Load older
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
